import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaRegStar, FaStar } from 'react-icons/fa';
import MediaUploader from '../../../Services/Media';
import '../../../styles/Review.scss';
import { tryFetch } from 'Helpers/FetchAPI';
import { getTokenOrDefault } from 'Helpers/Storage';
import { useSnackbar } from 'notistack';


type Media = {
  media_url: string;
};

type OrderItem = {
  item_id: string;
  product_id: string;
  product_name: string;
  product_variant_name?: string;
  media: Media[];
};

type Order = {
  order_id: string;
  store_name: string;
  store_logo: string;
  items: OrderItem[];
  order_reference: string;
};

const Review: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const [order, setOrder] = useState<any>(null);
  const [productReviews, setProductReviews] = useState<any[]>([]);
  const [merchantStars, setMerchantStars] = useState(0);
  const { enqueueSnackbar } = useSnackbar();


  useEffect(() => {
    const fetchOrder = async () => {
    if (!orderId) return;

    const token = getTokenOrDefault();
    const response = await tryFetch({
      url: `/g/orders/${orderId}`,
      method: 'GET',
      token,
    });

   if (
        response.is_success &&
        typeof response.data === 'object' &&
        response.data !== null &&
        'items' in response.data
      ) {
        const orderData = response.data as Order;
        setOrder(orderData);

        const initialReviews = orderData.items.map((item) => ({
          item_id: item.item_id,
          product_id: item.product_id,
          product_name: item.product_name,
          product_variant_name: item.product_variant_name,
          media: item.media,
          stars: 0,
          message: '',
          imageUrls: [],
        }));

        setProductReviews(initialReviews);
      } else {
        console.error('Invalid order data format:', response.data);
      }
    };

    fetchOrder();
  }, [orderId]);

  const handleProductRatingChange = (index: number, value: number) => {
    const updated = [...productReviews];
    updated[index].stars = value;
    setProductReviews(updated);
  };

  const handleProductMessageChange = (index: number, value: string) => {
    const updated = [...productReviews];
    updated[index].message = value;
    setProductReviews(updated);
  };

  // const handleProductImageUpload = (index: number, url: string) => {
  //   const updated = [...productReviews];
  //   updated[index].imageUrl = url;
  //   setProductReviews(updated);
  // };

  const handleProductImageUpload = (index: number, url: string) => {
  setProductReviews(prev => {
    const updated = [...prev];
    const product = updated[index];

    if (product.imageUrls.length >= 5) {
      enqueueSnackbar('You can only upload up to 5 images.', { variant: 'warning' });
      return prev;
    }

    product.imageUrls = [...product.imageUrls, url];
    return updated;
  });
};

  const handleSubmit = async () => {
     if (!orderId) return;

  const token = getTokenOrDefault();

  const payload = {
      merchant_rating: merchantStars.toString(),
      products: productReviews.map(product => ({
      product_id: product.product_id,
      product_variant_id: product.product_variant_name ? product.product_variant_id || null : null,
      product_rating: product.stars.toString(),
      product_remarks: product.message
    }))
  };

  const response = await tryFetch({
    url: `/g/orders/${orderId}/review`,
    method: 'POST',
    token,
    payload: payload,
  });

  if (response.is_success) {
     enqueueSnackbar('Review submitted successfully!', { variant: 'success' });
    navigate('/p/orders'); 
  } else {
    console.error('Failed to submit review:', response.error);
    enqueueSnackbar('Failed to submit review. Please try again.', { variant: 'error' });
  }
};

const isSubmitDisabled =
  merchantStars === 0 || productReviews.some((review) => review.stars === 0);

   const handleBackNavigation = () => {
    navigate(-1);
  };

  return (
    <div className="review-page">
      <div className="rev-header">
        <button className="rev-back-button" onClick={handleBackNavigation}>
          &larr; Back
        </button>
        <h2>Order Review</h2>
      </div>
      {order && (
        <>
          <p className="order-id">Reference No.: {order.order_reference}</p>
          <hr />
          {productReviews.map((product, index) => (
            <div key={product.product_id} className="product-review-block">
              <div className="review-flex">
                <img src={product.media[0]?.media_url} alt="product" className="product-img" />
                <div className="product-info">
                  <div className="product-top-row">
                  <h4>{product.product_name}</h4>
                   <div className="stars-rating">
                    {[1, 2, 3, 4, 5].map(star => (
                      <span
                        key={star}
                        onClick={() => handleProductRatingChange(index, star)}
                        className="star-icon"
                      >
                        {product.stars >= star ? <FaStar color="#3d3d3d" size={24} /> : <FaRegStar color="#3d3d3d" size={24} />}
                      </span>
                    ))}
              </div>
              </div>
                   {product?.product_variant_name && (
                    <p className="variant">Variant: {product.product_variant_name}</p>
                  )}
              </div>
              </div>

              <textarea
                placeholder="Write your review ..."
                value={product.message}
                onChange={(e) => handleProductMessageChange(index, e.target.value)}
                className="review-message"
              />
              <MediaUploader 
              onUploadSuccess={(url) => handleProductImageUpload(index, url)} 
              reference_type="PRODUCT_REVIEW"
              orderId={orderId}
              productId={product.product_id}
              currentImageCount={product.imageUrls.length}
              key={`${product.product_id}-${product.imageUrls.length}`} 
              />
              {/* {product.imageUrl && (
                <div className="uploaded-image-preview">
                  <img src={product.imageUrl} alt="Uploaded" className="uploaded-image" />
                </div>
              )} */}
              {product.imageUrls?.length > 0 && (
                <div className="uploaded-image-preview multiple">
                  {product.imageUrls.map((img: string, idx: number) => (
                    <img key={idx} src={img} alt={`Uploaded ${idx + 1}`} className="uploaded-image" />
                  ))}
                </div>
              )}
              <hr />
            </div>
          ))}

          <div className="merchant-review-block">
            <h3>Merchant Review</h3>
            <div className="review-flex">
              <img src={order.store_logo} alt="store logo" className="merchant-logo" />
              <div className="merchant-info">
                 <div className="product-top-row">
              <span>{order.store_name}</span>
            <div className="stars-rating">
              {[1, 2, 3, 4, 5].map(star => (
                <span
                  key={star}
                  onClick={() => setMerchantStars(star)}
                  className="star-icon"
                >
                  {merchantStars >= star ? <FaStar color="#3d3d3d" size={24} /> : <FaRegStar color="#3d3d3d" size={24} />}
                </span>
              ))}
              </div>
            </div>
          </div>
          </div>
          </div>

          <button onClick={handleSubmit} disabled={isSubmitDisabled} className="submit-button">
            SUBMIT REVIEW
          </button>
        </>
      )}
    </div>
  );
};

export default Review;